import {
  clearAccessToken,
  notifyInvalidAuthSession,
  readAccessToken,
} from '../auth/storage'

export const DEFAULT_API_BASE_URL = 'https://api.roborisen.com'

const FRIENDLY_API_MESSAGES: Readonly<Record<number, string>> = {
  401: '로그인이 만료되었습니다. 다시 로그인해 주세요.',
  403: '이 작업을 수행할 권한이 없습니다.',
  409: '진행 상태가 변경되었습니다. 최신 상태를 불러온 뒤 다시 시도해 주세요.',
  503: '서비스 연결이 일시적으로 원활하지 않습니다. 잠시 후 다시 시도해 주세요.',
}

export class ApiError extends Error {
  readonly status: number
  readonly method: string
  readonly path: string
  readonly detail: string

  constructor(method: string, path: string, status: number, detail = '') {
    super(
      FRIENDLY_API_MESSAGES[status] ??
        (status === 0
          ? '서버에 연결하지 못했습니다. 네트워크 상태를 확인해 주세요.'
          : `요청을 처리하지 못했습니다. (${status})`),
    )
    this.name = 'ApiError'
    this.method = method
    this.path = path
    this.status = status
    this.detail = detail
  }
}

export interface ApiRequestOptions {
  auth?: boolean
  clearSessionOnUnauthorized?: boolean
  signal?: AbortSignal
}

function redactSensitiveValues(value: string): string {
  return value
    .replace(/Bearer\s+[^\s"']+/gi, 'Bearer [redacted]')
    .replace(
      /\b[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\b/g,
      '[redacted-token]',
    )
    .slice(0, 1_000)
}

async function parseError(response: Response): Promise<string> {
  const text = await response.text()
  return redactSensitiveValues(text)
}

export function resolveApiBaseUrl(
  configuredBase = import.meta.env.VITE_API_BASE_URL,
): string {
  const configured = configuredBase?.trim()
  const base = configured || DEFAULT_API_BASE_URL
  return base.replace(/\/+$/, '')
}

export function buildApiUrl(
  path: string,
  configuredBase = import.meta.env.VITE_API_BASE_URL,
): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const base = resolveApiBaseUrl(configuredBase)

  // Tolerate legacy overrides that already include /api.
  if (base.endsWith('/api') && (normalizedPath === '/api' || normalizedPath.startsWith('/api/'))) {
    return `${base.slice(0, -4)}${normalizedPath}`
  }

  return `${base}${normalizedPath}`
}

async function apiRequest<T>(
  method: string,
  path: string,
  body: unknown,
  options: ApiRequestOptions,
): Promise<T> {
  const token = options.auth === false ? null : readAccessToken()
  const headers = new Headers({ Accept: 'application/json' })

  if (body !== undefined) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  let response: Response
  try {
    response = await fetch(buildApiUrl(path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: options.signal,
    })
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(method, path, 0)
  }

  if (!response.ok) {
    const detail = await parseError(response)
    if (
      response.status === 401 &&
      token &&
      readAccessToken() === token &&
      options.clearSessionOnUnauthorized !== false
    ) {
      clearAccessToken()
      notifyInvalidAuthSession()
    }
    throw new ApiError(method, path, response.status, detail)
  }

  if (response.status === 204) return null as T
  const text = await response.text()
  if (!text) return null as T

  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError(method, path, 502, 'Invalid JSON response')
  }
}

export function apiGet<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  return apiRequest<T>('GET', path, undefined, options)
}

export function apiPost<T>(
  path: string,
  body: unknown,
  options: ApiRequestOptions = {},
): Promise<T> {
  return apiRequest<T>('POST', path, body, options)
}

export function apiPatch<T>(
  path: string,
  body: unknown,
  options: ApiRequestOptions = {},
): Promise<T> {
  return apiRequest<T>('PATCH', path, body, options)
}

export function apiDelete<T>(
  path: string,
  body?: unknown,
  options: ApiRequestOptions = {},
): Promise<T> {
  return apiRequest<T>('DELETE', path, body, options)
}
